import { db } from '../database/firebase';
import { collection, getDocs, query, where, updateDoc, doc, getDoc } from 'firebase/firestore';
import { generateTicketID } from '../utils/formatting';
import moment from 'moment';
import { apiGet, apiPost, apiPut, useApiData } from './apiClient';
import { getFirstStockId } from '../utils/eventSessions';
import { getEventDbId } from '../utils/eventIdentity';
import { getStockById, getStocks } from './stocksService';
import { getSales } from './salesService';

/** Tamamlanmis satis (status=1) — bilet alinmadiysa event silinebilir. */
export const countCompletedPurchasesForEvent = async (eventId) => {
  if (!eventId) return 0;

  const sales = await getSales({ eventID: eventId });
  let count = sales.filter((s) => Number(s.status) === 1).length;
  if (count > 0) return count;

  if (useApiData) {
    try {
      const qs = new URLSearchParams({ perPage: '500' });
      qs.set('event.id', eventId);
      const res = await apiGet(`sales-v2?${qs.toString()}`);
      count = (res?.data ?? []).filter((s) => Number(s.status) === 1).length;
    } catch {
      // sales-v2 bos olabilir
    }
  }

  return count;
};

const EMPTY_EVENT_STATS = {
  totalRevenue: 0,
  totalSales: 0,
  totalTickets: 0,
  totalSeats: 0,
  totalRemaining: 0,
  totalCouponDiscount: 0,
  totalCommssionAmount: 0,
  totalAmountOfCashSales: 0,
};

const mapEventStatsPayload = (raw) => {
  const data =
    raw?.body && typeof raw.body === 'object' && raw.id !== 'counts' ? raw.body : raw ?? {};
  return {
    totalRevenue: data.totalRevenue ?? 0,
    totalSales: data.totalSales ?? 0,
    totalTickets: data.totalTickets ?? 0,
    totalSeats: data.totalSeats ?? 0,
    totalRemaining: data.totalRemaining ?? 0,
    totalCouponDiscount: data.totalCouponDiscount ?? 0,
    totalCommssionAmount: data.totalCommssionAmount ?? 0,
    totalAmountOfCashSales: data.totalAmountOfCashSales ?? 0,
  };
};

/**
 * Fetch event sales data from eventStats collection
 */
export const fetchEventSalesData = async (eventId) => {
  if (useApiData) {
    try {
      const row = await apiGet(`stats/${encodeURIComponent(eventId)}`);
      return mapEventStatsPayload(row);
    } catch (error) {
      if (error.status === 404) return { ...EMPTY_EVENT_STATS };
      throw error;
    }
  }

  const q = query(collection(db, 'eventStats'), where('event', '==', eventId));
  const querySnapshot = await getDocs(q);

  let eventStatsData = { ...EMPTY_EVENT_STATS };

  querySnapshot.forEach((snap) => {
    eventStatsData = mapEventStatsPayload(snap.data());
  });

  return eventStatsData;
};

/** Batch: GET stats?ids=a,b,c — EventRevenueCell / list hydrate */
export const fetchEventSalesDataBatch = async (eventIds = []) => {
  const ids = [...new Set((eventIds || []).map(String).filter(Boolean))].slice(
    0,
    100,
  );
  if (!ids.length) return {};

  if (useApiData) {
    try {
      const res = await apiGet(`stats?ids=${ids.map(encodeURIComponent).join(',')}`);
      const map = {};
      for (const row of res?.data ?? []) {
        if (!row?.id) continue;
        map[row.id] = mapEventStatsPayload(row);
      }
      return map;
    } catch (error) {
      console.warn('fetchEventSalesDataBatch failed', error?.message);
      return {};
    }
  }

  const entries = await Promise.all(
    ids.map(async (id) => [id, await fetchEventSalesData(id)]),
  );
  return Object.fromEntries(entries);
};

/**
 * Cancel an event (with optional background refunds)
 */
export const fetchCancelPreview = async (eventId) => {
  if (!useApiData) {
    return {
      salesCount: 0,
      ticketCount: 0,
      distinctBuyers: 0,
      totalAmount: 0,
    };
  }
  return apiGet(`events/${encodeURIComponent(eventId)}/cancel-preview`);
};

export const cancelEvent = async (
  eventId,
  {
    refund = true,
    reason,
    refundReasonMail,
    refundReasonSms,
    refundReasonPush,
    sendMail,
    sendSms,
    sendPush,
    notifyEmails,
    notifyPhones,
    notifyEmail,
    notifyPhone,
  } = {},
) => {
  if (useApiData) {
    return apiPost(`events/${encodeURIComponent(eventId)}/cancel`, {
      refund,
      reason: reason || undefined,
      refundReasonMail: refundReasonMail || undefined,
      refundReasonSms: refundReasonSms || undefined,
      refundReasonPush: refundReasonPush || undefined,
      sendMail,
      sendSms,
      sendPush,
      notifyEmails: notifyEmails?.length ? notifyEmails : undefined,
      notifyPhones: notifyPhones?.length ? notifyPhones : undefined,
      notifyEmail: notifyEmail || undefined,
      notifyPhone: notifyPhone || undefined,
    });
  }

  const payload = {
    isCancelled: true,
    isActive: false,
    isVerified: false,
  };
  await updateDoc(doc(db, 'events-v2', eventId), payload);
  return { ok: true, refund: false };
};

export const fetchCancelJob = async (eventId) => {
  if (!useApiData) return { data: null };
  return apiGet(`events/${encodeURIComponent(eventId)}/cancel-job`);
};

/**
 * Web / mobil anasayfa (home sections) görünürlüğünü günceller.
 */
export const setEventHomeVisibility = async (
  eventId,
  { hideFromWebHome, hideFromMobileHome },
) => {
  const payload = {};
  if (typeof hideFromWebHome === 'boolean') {
    payload.hideFromWebHome = hideFromWebHome;
  }
  if (typeof hideFromMobileHome === 'boolean') {
    payload.hideFromMobileHome = hideFromMobileHome;
  }
  if (Object.keys(payload).length === 0) return;

  if (useApiData) {
    await apiPut(`events/${encodeURIComponent(eventId)}`, payload);
    return;
  }

  await updateDoc(doc(db, 'events-v2', eventId), payload);
};

/**
 * Etkinligin organizatore odenecek net hak edisini ve odeme durumunu hesaplar.
 * stats: stats/{eventId} endpointinden gelen ozet veri.
 */
export const computeEventPayout = (event, stats = {}) => {
  const revenue = Number(stats.totalRevenue || 0);
  const coupon = Number(stats.totalCouponDiscount || 0);
  const remaining = Number(stats.totalRemaining || 0);
  const cash = Number(stats.totalAmountOfCashSales || 0);
  const commission = Number(stats.totalCommssionAmount || 0);

  const subTotal = revenue - coupon - remaining - cash;
  const payoutExtra = Number(event?.payoutExtra || 0);
  const settlementEntries = Array.isArray(event?.settlementEntries)
    ? event.settlementEntries
    : [];
  const settlementNet = settlementEntries.reduce((sum, e) => {
    const amount = Number(e?.amount || 0);
    if (e?.type === 'income') return sum + amount;
    if (e?.type === 'expense') return sum - amount;
    return sum;
  }, 0);
  const baseNet = event?.isCommissionExtra ? subTotal : subTotal - commission;
  const netOwed = baseNet + payoutExtra + settlementNet;
  const paid = Number(event?.paidAmount || 0);
  const remainingToPay = netOwed - paid;

  return {
    revenue,
    coupon,
    remaining,
    cash,
    commission,
    subTotal,
    payoutExtra,
    settlementNet,
    netOwed,
    paid,
    remainingToPay,
  };
};

/**
 * Make payment for an event.
 * payment: { paidAmount, payNote, isPaid } — paidAmount birikimli toplam odenen tutardir.
 */
export const makeEventPayment = async (eventId, payment = {}) => {
  const {
    actionType = 'pay',
    paymentType = 'event',
    paymentStage = null,
    paidAmount = 0,
    payoutExtra,
    payoutExtraByType,
    paidNow = 0,
    payNote = '',
    isPaid = true,
  } = payment;

  const payload = {
    isPaid,
    paidAmount,
    payNote,
  };
  if (payoutExtra != null) {
    payload.payoutExtra = payoutExtra;
  }
  if (payoutExtraByType != null) {
    payload.payoutExtraByType = payoutExtraByType;
  }

  if (useApiData) {
    if (Number(paidNow) > 0) {
      await apiPost('payments', {
        total: Number(paidNow),
        status: 1,
        paymentType,
        event: { id: eventId },
        transaction: {
          type: 'manual-panel',
          source: 'event-payout',
          operation: actionType,
          paymentStage: paymentStage || paymentType,
          createdAt: Date.now(),
          note: payNote ?? '',
        },
      });
    }
    await apiPut(`events/${encodeURIComponent(eventId)}`, payload);
    return;
  }

  await updateDoc(doc(db, 'events-v2', eventId), payload);
};

/**
 * Sale report gider/gelir kalemini event.settlementEntries'e ekler.
 */
export const addEventSettlementEntry = async (eventId, entry, existingEntries = []) => {
  const list = Array.isArray(existingEntries) ? [...existingEntries] : [];
  const next = {
    id: entry.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    type: entry.type === 'expense' ? 'expense' : 'income',
    amount: Number(entry.amount) || 0,
    note: entry.note || '',
    categoryId: entry.categoryId || null,
    categoryName: entry.categoryName || '',
    categoryCode: entry.categoryCode || '',
    documents: Array.isArray(entry.documents) ? entry.documents : [],
    createdAt: entry.createdAt || Date.now(),
    createdBy: entry.createdBy || null,
  };
  list.push(next);

  const payload = { settlementEntries: list };
  if (useApiData) {
    await apiPut(`events/${encodeURIComponent(eventId)}`, payload);
    return next;
  }
  await updateDoc(doc(db, 'events-v2', eventId), payload);
  return next;
};

/**
 * Settlement kalemini siler.
 */
export const deleteEventSettlementEntry = async (eventId, entryId, existingEntries = []) => {
  const list = (Array.isArray(existingEntries) ? existingEntries : []).filter(
    (e) => String(e.id) !== String(entryId),
  );
  const payload = { settlementEntries: list };
  if (useApiData) {
    await apiPut(`events/${encodeURIComponent(eventId)}`, payload);
    return list;
  }
  await updateDoc(doc(db, 'events-v2', eventId), payload);
  return list;
};

/**
 * Clone event data preparation
 */
const loadStockForClone = async (record) => {
  let stockID = getFirstStockId(record);

  if (useApiData) {
    let stockData = null;
    if (stockID) {
      stockData = await getStockById(stockID);
    }
    if (!stockData && getEventDbId(record)) {
      const stocks = await getStocks({ eventID: getEventDbId(record), limitCount: 50 });
      if (stocks.length) {
        stockData =
          stocks.find((s) => s.id === stockID || s.stockID === stockID) ?? stocks[0];
      }
    }
    if (!stockData?.stock) {
      throw new Error('Event has no stock session to clone from');
    }
    return stockData;
  }

  if (!stockID && getEventDbId(record)) {
    const stocks = await getStocks({ eventID: getEventDbId(record), limitCount: 1 });
    stockID = stocks[0]?.id ?? stocks[0]?.stockID ?? null;
  }
  if (!stockID) {
    throw new Error('Event has no stock session to clone from');
  }

  const stockDocSnap = await getDoc(doc(db, 'stocks', stockID));
  if (!stockDocSnap.exists()) {
    throw new Error('Stock data not found for clone');
  }
  return { id: stockDocSnap.id, ...stockDocSnap.data() };
};

export const prepareEventForCloning = async (record, permissions) => {
  const stockData = await loadStockForClone(record);

  // Construct tickets based on event type
  let tickets = null;
  let eventType = record.type;

  if (eventType === 3) {
    tickets = [];
    const seatedStock = Array.isArray(stockData.stock) ? stockData.stock : [];
    if (seatedStock.length > 0) {
      seatedStock.forEach((ticket) => {
        let ticketClone = { ...ticket };
        ticketClone.id = generateTicketID();
        tickets.push(ticketClone);
      });
      record.tickets = tickets;
    }
    record.sessions = [];
  } else {
    tickets = {};
    const stockObj =
      stockData.stock && typeof stockData.stock === 'object' && !Array.isArray(stockData.stock)
        ? stockData.stock
        : {};
    Object.keys(stockObj).forEach((key) => {
      let ticketGroup = stockObj[key];
      if (ticketGroup && ticketGroup.length > 0) {
        let ticketGroupClone = [];
        ticketGroup.forEach((ticket) => {
          let ticketClone = { ...ticket };
          ticketClone.stock = ticket.stock ? ticket.stock : 0;
          ticketClone.sold = 0;
          ticketGroupClone.push(ticketClone);
        });
        tickets[key] = ticketGroupClone;
      } else {
        tickets[key] = [];
      }
    });
    record.tickets = tickets;
  }

  let coupons = null;
  if (record.coupons && record.coupons.length > 0) {
    coupons = [];
    record.coupons.forEach((coupon) => {
      let couponClone = { ...coupon };
      if (couponClone.used === undefined || couponClone.used === null) {
        couponClone.used = 0;
        couponClone.stock = 0;
      } else {
        couponClone.used = 0;
      }
      couponClone.activeUntil = null;
      coupons.push(couponClone);
    });
  }

  let layoutID = null;
  let layoutJSONID = null;
  if (record.layoutID && record.layoutJSONID) {
    layoutID = record.layoutID;
    layoutJSONID = record.layoutJSONID;
  }

  return {
    category: record.category,
    city: record.city,
    coupons: coupons,
    description: record.description,
    details: record.details,
    layoutID: layoutID,
    layoutJSONID: layoutJSONID,
    name: record.name ? `${record.name} (Copy)` : '',
    organisationCompanies: record.organisationCompanies ?? [],
    paymentMethods: record.paymentMethods ?? [],
    rules: record.rules ?? {},
    subcategories: record.subcategories ?? [],
    tickets: tickets,
    venue: record.venue,
    venueLayout: null,
    videoURL: record.videoURL ?? null,
    isCommissionExtra: record.isCommissionExtra ? record.isCommissionExtra : false,
    commissionFee: record.commissionFee ? record.commissionFee : 0,
    servicefee: record.servicefee ? record.servicefee : 0,
    isCloned: true,
    dateType: record.dateType,
    duration: record.duration,
    type: record.type,
    startdate: null,
    enddate: null,
    isActive: false,
    isVerified: false,
    isCancelled: false,
    banner: null,
    createdate: moment().toDate(),
    createdby: permissions && permissions.email ? permissions.email : record.createdby,
    lastupdate: moment().toDate(),
    updatedby: permissions && permissions.email ? permissions.email : record.createdby,
    hasSentNotification: false,
    notify: true,
  };
};

const eventService = {
  fetchEventSalesData,
  fetchEventSalesDataBatch,
  cancelEvent,
  setEventHomeVisibility,
  makeEventPayment,
  addEventSettlementEntry,
  deleteEventSettlementEntry,
  computeEventPayout,
  prepareEventForCloning,
  countCompletedPurchasesForEvent,
};

export default eventService;
